import * as _ from 'lodash'
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {
    WB_DimensionRow, WB_MetricDefinitionRow, WB_PeriodRow,
    WB_ReportMetricRow,
    WB_ReportRow,
} from "../PA.Core/PAdbSchemaRows";
import {
    IConnection,
    IDimension,
    IDimensionSelector,
    IHierarchyNode,
    IWorkspaceReportContext
} from "../PA.Reporting.Core/PA.Reporting.IF";
import {IPolicy} from "../PA.Core/PA.Core.IF.Policy";
import {DimensionSelector} from "../PA.Reporting.Core/DimensionSelector";
import {DimensionDataService} from "../PA.Reporting.Services/DimensionDataService";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Dimension,
    esWB_Period,
    esWB_Report,
    esWB_Report_Metric,
    esWB_Company,
    esWB_Currency, esWB_Metric_Definition
} from "../PA.Core/PAdbSchema";
import {IDataSetRequest} from "../PA.DataSet.Services/PA.DataSet.Services.IF";
import {DataSetService} from "../PA.DataSet.Services/DataSetService";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {PAusrContext} from "../PA.Core/PAusrContext";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export interface IWorkspaceReportContextOptions {
    ReportRow?: WB_ReportRow,
    ReportRows?: WB_ReportRow[],
    wbReportId?: number,
    wbReportIds?: number[]
}

const WorkspaceReportContextOptions_default: IWorkspaceReportContextOptions = {}

export class WorkspaceReportContext
    implements IWorkspaceReportContext {
    LoadOptions: IWorkspaceReportContextOptions
    _reportRows: WB_ReportRow[]
    _wbIDReportSelected: number
    _onWorkspaceContextLoaded: BehaviorSubject<IWorkspaceReportContext>
    _dsCustomer: IDimensionSelector
    _dsProduct: IDimensionSelector
    _dsTime: IDimensionSelector
    _aggLevelCustomer: string
    _aggLevelProduct: string
    _aggLevelTime: string
    _reportLayoutSelected: string
    _onResultsChanged: BehaviorSubject<IWorkspaceReportContext>
    _policy: IPolicy
    _results: any[]
    _wbSnapshotIDSelected: number
    _currency: string
    _wbCompanyIDSelected: number
    _bHierarchiesLoaded: boolean
    _bExecuted: boolean

    dbContext: PAdbContext;

    constructor(options?: IWorkspaceReportContextOptions) {
        let self = this

        this.dbContext = new PAdbContext({});
        this._onResultsChanged = new BehaviorSubject(this);
        this._onWorkspaceContextLoaded = new BehaviorSubject(this);
        this.LoadOptions = options || {}
        this._bExecuted = false
        _.defaults(this.LoadOptions, WorkspaceReportContextOptions_default)

        this._bHierarchiesLoaded = false

        let connection: IConnection = {
            ConnectionId: "Reporting",
            ConnectionName: "Reporting",
            ConnectionOptions: {"connectionName": "PAhdb"},
            ConnectionTypeId: "Reporting"
        }
        let customerDimension: IDimension = {
            DimensionId: "/AZR/PLNCST",
            DimensionName: "Customer",
            DimensionRenderOptions: {},
            DimensionTechnicalName: "/AZR/PLNCST"
        }
        let productDimension: IDimension = {
            DimensionId: "/AZR/FNSPRD",
            DimensionName: "Product",
            DimensionRenderOptions: {},
            DimensionTechnicalName: "/AZR/FNSPRD"
        }
        let timeDimension: IDimension = {
            DimensionId: "/AZR/DATE",
            DimensionName: "Time",
            DimensionRenderOptions: {},
            DimensionTechnicalName: "/AZR/DATE"
        }

        async function getRenderOptions() {
            let custDimRenderOptions = await self.getDimRenderOptions("PlanningCustomerReporting")
            if (!_.isEmpty(custDimRenderOptions))
                customerDimension.DimensionRenderOptions = custDimRenderOptions;

            let prodDimRenderOptions = await self.getDimRenderOptions("FinishedProductReporting")
            if (!_.isEmpty(prodDimRenderOptions))
                productDimension.DimensionRenderOptions = prodDimRenderOptions;

            let timeDimRenderOptions = await self.getDimRenderOptions("TimeReporting")
            if (!_.isEmpty(timeDimRenderOptions))
                timeDimension.DimensionRenderOptions = timeDimRenderOptions;

            function sleep(ms: number) {
                return new Promise(resolve => setTimeout(resolve, ms)); // create a promise that resolves after ms milliseconds
            }

            let customerDimensionDataProvider = new DimensionDataService(connection, customerDimension);

            let productDimensionDataProvider = new DimensionDataService(connection, productDimension);

            let timeDimensionDataProvider = new DimensionDataService(connection, timeDimension);

            self._dsCustomer = new DimensionSelector(customerDimension, customerDimensionDataProvider);
            await self._dsCustomer.OnLoadComplete

            await sleep(3000)
            self._dsProduct = new DimensionSelector(productDimension, productDimensionDataProvider);
            await self._dsProduct.OnLoadComplete

            await sleep(3000)
            self._dsTime = new DimensionSelector(timeDimension, timeDimensionDataProvider);
            await self._dsTime.OnLoadComplete

        }

        getRenderOptions()
            .then(() => {
                this._aggLevelCustomer = '';
                this._aggLevelProduct = '';
                this._aggLevelTime = '';
                this._reportLayoutSelected = '';
                this._wbSnapshotIDSelected = 0;
                let wbReportIdsToLoad: number[] = [];
                if ('wbReportIds' in this.LoadOptions) {
                    wbReportIdsToLoad = this.LoadOptions.wbReportIds;
                }
                let query = {};
                if (wbReportIdsToLoad.length > 0)
                    query = {inq: {WB_REPORT_ID: wbReportIdsToLoad}};

                this.dbContext.findAll(esWB_Report, query)
                    .then((arrReports) => {
                        self._reportRows = arrReports;
                        self._bHierarchiesLoaded = true
                        self._onWorkspaceContextLoaded.next(this);
                    })
                    .catch((err) => {
                        Error("Couldn't load the reports :" + err);
                    });
            })
            .catch((err) => {
                Error("Couldn't load the reports :" + err);
                self._onWorkspaceContextLoaded.next(this);
            });
    }

    get bHierarchiesLoaded(): boolean {
        return this._bHierarchiesLoaded
    }

    get dsCustomer(): IDimensionSelector {
        return this._dsCustomer
    }

    set dsCustomer(value: IDimensionSelector) {
        this._dsCustomer = value
    }

    get dsProduct(): IDimensionSelector {
        return this._dsProduct
    }

    set dsProduct(value: IDimensionSelector) {
        this._dsProduct = value
    }

    get dsTime(): IDimensionSelector {
        return this._dsTime
    }

    set dsTime(value: IDimensionSelector) {
        this._dsTime = value
    }

    get Currency(): string {
        return this._currency
    }

    set Currency(value: string) {
        this._currency = value
    }


    get WB_ReportRows(): WB_ReportRow[] {
        return this._reportRows
    }

    set WB_ReportRows(arrReports: WB_ReportRow[]) {
        this._reportRows = arrReports
    }

    get WB_Report_ID_Selected(): number {
        return this._wbIDReportSelected
    }

    set WB_Report_ID_Selected(value: number) {
        this._wbIDReportSelected = value
    }

    get WB_Snapshot_ID_Selected(): number {
        return this._wbSnapshotIDSelected
    }

    set WB_Snapshot_ID_Selected(value: number) {
        this._wbSnapshotIDSelected = value
    }

    get WB_Company_ID_Selected(): number {
        return this._wbCompanyIDSelected
    }

    set WB_Company_ID_Selected(value: number) {
        this._wbCompanyIDSelected = value
    }

    get OnWorkspaceContextLoaded(): BehaviorSubject<IWorkspaceReportContext> {
        return this._onWorkspaceContextLoaded
    }

    get aggLevelCustomer(): string {
        return this._aggLevelCustomer
    }

    set aggLevelCustomer(value: string) {
        this._aggLevelCustomer = value
    }

    get aggLevelProduct(): string {
        return this._aggLevelProduct
    }

    set aggLevelProduct(value: string) {
        this._aggLevelProduct = value
    }

    get aggLevelTime(): string {
        return this._aggLevelTime
    }

    set aggLevelTime(value: string) {
        this._aggLevelTime = value
    }

    get Policy(): IPolicy {
        return this._policy
    }

    get Results(): any[] {
        return this._results
    }

    set Results(arrResults: any[]) {
        this._results = arrResults
    }

    get bExecuted(): boolean {
        return this._bExecuted
    }

    set bExecuted(value: boolean) {
        this._bExecuted = value
    }

    getDimRenderOptions(sDimID: string): Promise<any> {
        const self = this
        let defaultRenderOptions = {}
        return new Promise<any>((resolve, error) => {
            self.dbContext.findAll(esWB_Dimension, {where: {DIMENSION_ID: sDimID}})
                .then((arrDimRows: WB_DimensionRow[]) => {
                    if (_.isEmpty(arrDimRows))
                        resolve(defaultRenderOptions)
                    else {
                        resolve(JSON.parse(arrDimRows[0].cif.RENDER_OPTIONS))
                    }

                })
                .catch(err => {
                    resolve(defaultRenderOptions)
                })
        })
    }

    async Execute(): Promise<void> {
        const self = this

        try {
            let selReportID: number = self._wbIDReportSelected
            let companies = [self._wbCompanyIDSelected]
            let arrReportMetricRows: WB_ReportMetricRow[] = <WB_ReportMetricRow[]>await self.dbContext.findAll(esWB_Report_Metric,
                {where: {WB_REPORT_ID: selReportID, DELETED_IND: {neq: '1'}}})
            //
            let wbSnapshotIDSelected = self._wbSnapshotIDSelected
            let reportMetricIds: number[] = _.map(arrReportMetricRows, (a) => a.cif.WB_MTRDFN_ID)
            let metricDefinitions = <WB_MetricDefinitionRow[]>await self.dbContext.findUsingCache(esWB_Metric_Definition, reportMetricIds)
            let userContext: PAusrContext = PAEnvironment.getValue(PAEnvironment.UserContext)

            let aggKeysObj: {
                [dimensionID: string]: {
                    level: string,
                    hierarchyDate?: Date
                    wbHierarchyId?: number
                }
            } = {}
            if (self.aggLevelCustomer != '')
                aggKeysObj["WB_CUST_ID"] = {
                    level: self.aggLevelCustomer,
                    hierarchyDate: new Date(),
                    wbHierarchyId: _.toNumber(self.dsCustomer.HierarchySelected.HierarchyId)
                }

            if (self.aggLevelProduct != '')
                aggKeysObj["WB_PRDGRP_ID"] = {
                    level: self.aggLevelProduct,
                    hierarchyDate: new Date(),
                    wbHierarchyId: _.toNumber(self.dsProduct.HierarchySelected.HierarchyId)
                }

            if (self.aggLevelTime != '')
                aggKeysObj["WB_PERIOD_ID"] = {
                    level: self.aggLevelTime,
                    hierarchyDate: new Date(),
                    wbHierarchyId: _.toNumber(self.dsTime.HierarchySelected.HierarchyId)
                }

            let filterKeysObj: {
                [dimensionID: string]: {
                    wbHierarchyId?: number,
                    hierarchyDate?: Date,
                    nodes: { node_id: number | string, node_name?: string }[]
                }
            } = {}

            let custNodes: { node_id: number | string, node_name?: string }[] =
                _.map(self.dsCustomer.HierarchyNodesSelected, (aNode: IHierarchyNode) => {
                    return {node_id: aNode.NodeId}
                });

            let prodNodes: { node_id: number | string, node_name?: string }[] = []
            _.forEach(self.dsProduct.HierarchyNodesSelected, (aNode: IHierarchyNode) => {
                prodNodes.push({
                    node_id: aNode.NodeId
                })
            })

            let timeNodes: { node_id: number | string, node_name?: string }[] = []
            _.forEach(self.dsTime.HierarchyNodesSelected, (aNode: IHierarchyNode) => {
                timeNodes.push({
                    node_id: aNode.NodeId
                })
            })

            let securityKeysObject: {
                [dimensionID: string]: {
                    wbHierarchyId?: number,
                    hierarchyDate?: Date,
                    nodes: { node_id: number | string, node_name?: string }[]
                }
            } = {}

            // limit customers to the ones assigned to the user
            // if the user has no customers assigned, then don't return any data
            let custSecurityNodes: { node_id: number | string, node_name?: string }[] =
                _.map(userContext.Customers, (aCust) => {
                    return {node_id: aCust.cif.WB_CUST_ID}
                })

            filterKeysObj["WB_CUST_ID"] = {
                nodes: custNodes,
                hierarchyDate: new Date(),
                wbHierarchyId: _.toNumber(self.dsCustomer.HierarchySelected.HierarchyId)
            }
            filterKeysObj["WB_PRDGRP_ID"] = {
                nodes: prodNodes,
                hierarchyDate: new Date(),
                wbHierarchyId: _.toNumber(self.dsProduct.HierarchySelected.HierarchyId)
            }
            filterKeysObj["WB_PERIOD_ID"] = {
                nodes: timeNodes,
                hierarchyDate: new Date(),
                wbHierarchyId: _.toNumber(self.dsTime.HierarchySelected.HierarchyId)
            }

            securityKeysObject["WB_CUST_ID"] = {
                nodes: custSecurityNodes,
                hierarchyDate: new Date(),
                wbHierarchyId: _.toNumber(self.dsCustomer.HierarchySelected.HierarchyId)
            }

            let oRequest: IDataSetRequest = {
                AGGREGATION_KEYS: aggKeysObj,
                FILTER_KEYS: filterKeysObj,
                SECURITY_KEYS: securityKeysObject,
                WB_MTRDFN_ID: reportMetricIds,
                WB_SNAPSHOT_ID: wbSnapshotIDSelected,
                WB_COMPANY_ID: companies
            }
            console.log(oRequest)

            let aDataSetService: DataSetService = new DataSetService()
            let data = await aDataSetService.getDataSet(oRequest)

            let metricColNames = _.map(metricDefinitions, (a) => a.cif.METRIC_ID.toUpperCase().trim());

            for (let aRow of (data.Results || [])) {
                for (let aMetricId of metricColNames) {
                    aRow[aMetricId] = +(aRow[aMetricId])
                }
            }

            async function addPeriodStartDt() {
                for (let a = 0; a < data.Results.length; a++) {
                    let aResult = data.Results[a]
                    aResult["PERIOD_START_DT"] = ""
                    await self.dbContext.find(esWB_Period, aResult.WB_PERIOD_ID)
                        .then((aPeriodRow: WB_PeriodRow) => {
                            if (aPeriodRow)
                                aResult["PERIOD_START_DT"] = DateUtils.convertFromSAPBW(aPeriodRow.cif.PERIOD_START_DT)
                        }).catch((err) => {
                            console.log(err.toString());
                        })
                }
                self._results = _.sortBy(data.Results, "PERIOD_START_DT")
                self.Currency = ""
                let aReport = _.first(_.filter(self.WB_ReportRows, aRow => aRow.cif.WB_REPORT_ID == self._wbIDReportSelected))
                if (_.isNumber(aReport.cif.WB_COMPANY_ID) && aReport.cif.WB_COMPANY_ID > 0) {
                    let aCompany = await self.dbContext.find(esWB_Company, aReport.cif.WB_COMPANY_ID)
                    if (!_.isEmpty(aCompany)) {
                        let aCurrency = await self.dbContext.find(esWB_Currency, aCompany.cif.WB_CURRENCY_ID)
                        if (!_.isEmpty(aCurrency)) {
                            self.Currency = aCurrency.cif.CURRENCY_SYMBOL_POSITION == "Prefix" ? aCurrency.cif.CURRENCY_SYMBOL + '  ' : '  ' + aCurrency.cif.CURRENCY_SYMBOL
                        }
                    }
                }

                self._onResultsChanged.next(self)

            }

            if (!_.isEmpty(data.Results))
                await addPeriodStartDt()
            else {
                self.Currency = ""
                self._results = data.Results
                self._onResultsChanged.next(this)
            }

            this._bExecuted = true
        } catch (err) {
            self._results = []
            self._onResultsChanged.next(self)
            console.log(err.toString());
        }
    }

    get OnResultsChanged():
        BehaviorSubject<IWorkspaceReportContext> {
        return this._onResultsChanged
    }

}
